import {
  always,
  either,
  groupBy,
  pick,
  prop,
  uniq,
} from 'ramda'
import { createSelector } from 'redux-bundler'

import createEntityBundle, { asyncActionFactory } from '~/src/Lib/createEntityBundle'
import { EMPTY_ARRAY, EMPTY_OBJECT } from '~/src/Lib/Utils'
import { Device as schema, facilityScope as scope } from '~/src/Store/Schemas'

import { prepareData } from './shape'

const name = 'devices'
const bulkUpdateAction = asyncActionFactory({
  action: 'bulk_update',
  name,
  schema: [schema],
  snackbar: ({ payload, dispatch, store }) => {
    const { room: roomId, zone: zoneId, devices: deviceIds } = payload
    const facility = store.selectCurrentFacility().name
    const room = store.selectRooms()[roomId]
    const zone = store.selectZones()[zoneId]
    const devices = Object.values(store.selectDevices())
    const deviceSNs = deviceIds.map(id => {
      const device = devices.find(d => d.id === id)
      return device && device.serialNumber
    })
    let msg
    if (roomId && room) {
      msg = `
        Successfully assigned devices [${deviceSNs.join(', ')}] to room "${room.name}"
      `
      if (zoneId && zone) msg += `, zone "${zone.name}"`
    } else {
      msg = `Successfully assigned devices to facility "${facility}"`
    }
    dispatch({
      actionCreator: 'doAddSnackbarMessage',
      args: [msg],
    })
  },
  handler: ({ payload, apiFetch }) => apiFetch('/devices/bulk/', payload, { method: 'POST' }),
})

const moveUpdateAction = asyncActionFactory({
  action: 'move_device',
  name,
  schema: [schema],
  handler: ({ payload, apiFetch }) => apiFetch('/devices/move/', payload, { method: 'POST' }),
})

const initialBundle = createEntityBundle({
  name,
  apiConfig: { prepareData, schema },
  scope,
})
export default {
  ...initialBundle,
  reducer: (state, action) => {
    switch (action.type) {
      case bulkUpdateAction.types.start:
        return {
          ...state,
          bulk: {
            ...state.bulk,
            saving: true,
            success: null,
          },
        }
      case bulkUpdateAction.types.fail:
        return {
          ...state,
          bulk: {
            ...state.bulk,
            saving: false,
            success: false,
            error: action.error,
          },
        }
      case bulkUpdateAction.types.succeed:
        return {
          ...state,
          bulk: {
            ...state.bulk,
            saving: false,
            success: true,
          },
        }

      case moveUpdateAction.types.start:
        return {
          ...state,
          move: {
            ...state.move,
            saving: true,
            success: null,
          },
        }
      case moveUpdateAction.types.fail:
        return {
          ...state,
          move: {
            ...state.move,
            saving: false,
            success: false,
            error: action.error,
          },
        }
      case moveUpdateAction.types.succeed:
        return {
          ...state,
          move: {
            ...state.move,
            saving: false,
            success: true,
          },
        }

      default:
        return initialBundle.reducer(state, action)
    }
  },
  [bulkUpdateAction.actionName]: bulkUpdateAction,
  [moveUpdateAction.actionName]: moveUpdateAction,
  selectAvailableModels: createSelector(
    'selectDevices',
    'selectModels',
    (devices, models) => {
      if (!devices || !models) return EMPTY_OBJECT
      const deviceModels = uniq(Object.values(devices).map(prop('modelKey')))
      return pick(deviceModels, models)
    }
  ),
  selectDevicesByModel: createSelector(
    'selectDevices',
    'selectModels',
    (devices, models) => {
      if (!devices || !models) return EMPTY_OBJECT
      return Object.values(devices).filter(device => device.modelKey in models).reduce((dbm, device) => ({
        ...dbm,
        [device.modelKey]: [
          ...(dbm[device.modelKey] ?? []),
          device,
        ],
      }), {})
    },
  ),
  selectDevicesByRoom: createSelector(
    'selectDevices',
    'selectRooms',
    (devices, rooms) => {
      if (!devices) return EMPTY_OBJECT
      return Object.values(devices).filter(device => device.room in rooms).reduce((dbr, device) => ({
        ...dbr,
        [device.room]: [
          ...(dbr[device.room] ?? []),
          device,
        ],
      }), {})
    }
  ),
  selectDevicesByRoomAndZone: createSelector(
    'selectDevicesByRoom',
    /**
     * Given devices grouped by room ID, returns devices by room ID sub-grouped by zone ID and no zone ID.
     * @param {Object<number, Object[]>} byRoom
     * @returns {Object<number, Object<number|string, Object[]>>}
     */
    byRoom => {
      if (byRoom === EMPTY_OBJECT) return byRoom
      return Object.entries(byRoom).reduce((byRoomAndZone, [roomId, roomDevices]) => {
        const { room: devices = EMPTY_ARRAY, ...byZone } = groupBy(either(prop('zone'), always('room')), roomDevices)
        byRoomAndZone[roomId] = { ...byZone, devices }
        return byRoomAndZone
      }, {})
    }
  ),
}
